import React, { useState, useEffect, useRef } from 'react';
import {
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Input,
    Spinner,
    Table,
    Modal,
    ModalBody,
    Badge,
} from 'reactstrap';
import FeatherIconCom from '../../../Utils/CommonComponents/CommonIcons/FeatherIconCom';
import { getData } from '../../../CommonElements/Functions/PostGetFun';
import { API } from '../../../api/API';
import moment from 'moment';
import Select from 'react-select';
import Loader from '../../../Layout/Loader';

export default function PODSummary() {
    const [positionOptions, setPositionOptions] = useState<any[]>([]);
    const [selectedPosition, setSelectedPosition] = useState<string>('');
    const [selectedMonth, setSelectedMonth] = useState<string>(moment().format('YYYY-MM'));
    const [loading, setLoading] = useState<boolean>(false);
    const [podSummaryList, setPodSummaryList] = useState<any[]>([]);

    // Modal state for status details
    const [statusModalOpen, setStatusModalOpen] = useState<boolean>(false);
    const [statusDetails, setStatusDetails] = useState<any>({});

    // Modal state for attachments
    const [mediaModalOpen, setMediaModalOpen] = useState<boolean>(false);
    const [mediaLoading, setMediaLoading] = useState<boolean>(false);
    const [currentMedia, setCurrentMedia] = useState<{ images: any[]; pdfs: any[] }>({ images: [], pdfs: [] });
    const [selectedMediaTab, setSelectedMediaTab] = useState<'images' | 'pdfs'>('images');
    const mediaMetaCache = useRef<Map<string, any>>(new Map());

    const monthOptions = Array.from({ length: 6 }).map((_, i) => {
        const d = moment().subtract(i, 'months');
        return { label: d.format('MMM YYYY'), value: d.format('YYYY-MM') };
    });

    const fetchPositions = async () => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        try {
            const url = `${serverUrl}${API.getPositionTreeView}`;
            const response = await getData(url, true);
            const flat: any[] = [];
            const flatten = (node: any) => {
                if (!node) return;
                const empName = (node.EmployeeName || node.employee_name || node.Employee_Name || node.employeeName || node.name || '').trim();
                const posName = (node.position_name || node.positionName || '').trim();
                const label = [posName, empName].filter(Boolean).join(' - ');
                flat.push({ label: label || 'Unknown', value: String(node.position_id ?? '') });
                if (Array.isArray(node.children)) {
                    node.children.forEach((child: any) => flatten(child));
                }
            };
            const treeData = response?.data?.data || response?.data;
            if (treeData) {
                flatten(treeData);
                setPositionOptions(flat);
                const userPositionId = localStorage.getItem("positionId");
                if (userPositionId && flat.some((p: any) => String(p.value) === String(userPositionId))) {
                    setSelectedPosition(String(userPositionId));
                } else if (flat.length > 0) {
                    setSelectedPosition(flat[0].value);
                }
            }
        } catch (err) {
            console.error("Error fetching position tree:", err);
        }
    };

    const fetchPodSummary = async (position_id: string, month: string) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl || !position_id) return;
        setLoading(true);
        try {
            const url = `${serverUrl}${API.getPodSummary}`;
            const response = await getData(url, true, { position_id, month });
            if (response.status === 200 || response.data?.statusCode === 200) {
                const list = Array.isArray(response.data) ? response.data : (Array.isArray(response.data?.data) ? response.data.data : []);
                setPodSummaryList(list);
            } else {
                setPodSummaryList([]);
            }
        } catch (err) {
            console.error("Error fetching POD summary:", err);
            setPodSummaryList([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPositions();
    }, []);

    useEffect(() => {
        if (selectedPosition && selectedMonth) {
            fetchPodSummary(selectedPosition, selectedMonth);
        }
    }, [selectedPosition, selectedMonth]);

    const getStatusBadge = (status: string) => {
        const s = (status || '').toLowerCase();
        if (s === 'approved') return <Badge color="success" className="px-2 py-1">Approved</Badge>;
        if (s === 'rejected') return <Badge color="danger" className="px-2 py-1">Rejected</Badge>;
        return <Badge color="warning" className="px-2 py-1">Submitted</Badge>;
    };

    const resolveMediaByIds = async (mediaIdsStr: string) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl || !mediaIdsStr) return;
        setMediaLoading(true);
        const ids = String(mediaIdsStr).split(',').map(s => s.trim()).filter(Boolean);
        const images: any[] = [];
        const pdfs: any[] = [];

        for (const id of ids) {
            try {
                let meta = mediaMetaCache.current.get(id);
                if (!meta) {
                    const url = `${serverUrl}${API.downloadFile}?mediaid=${id}`;
                    const res = await getData(url, true);
                    meta = res?.data || {};
                    mediaMetaCache.current.set(id, meta);
                }
                const isPdf = meta?.mediatype && String(meta.mediatype).toLowerCase().includes('pdf');
                const fileUrl = meta?.url || `${serverUrl}media?id=${id}`;
                if (isPdf) {
                    pdfs.push({ id, pdfurl: meta?.pdfurl || fileUrl, name: meta?.name || `Document-${id}` });
                } else {
                    images.push({ id, url: fileUrl });
                }
            } catch (e) {
                images.push({ id, url: `${serverUrl}media?id=${id}` });
            }
        }

        setCurrentMedia({ images, pdfs });
        setSelectedMediaTab(images.length > 0 ? 'images' : 'pdfs');
        setMediaLoading(false);
        setMediaModalOpen(true);
    };

    return (
        <div className="p-3" style={{ color: '#000000' }}>
            {/* Filter Toolbar */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3 p-3 bg-light border rounded">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div style={{ minWidth: '320px' }}>
                        <label className="form-label small fw-bold text-dark mb-1" style={{ color: '#000000' }}>Select Position / Member</label>
                        <Select
                            options={positionOptions}
                            value={positionOptions.find((p) => String(p.value) === String(selectedPosition)) || null}
                            onChange={(opt: any) => setSelectedPosition(opt ? String(opt.value) : '')}
                            isSearchable={true}
                            placeholder="Search Position / Member..."
                            className="text-dark fw-semibold"
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderColor: '#dee2e6',
                                    minHeight: '38px',
                                    color: '#000000',
                                }),
                                singleValue: (base) => ({
                                    ...base,
                                    color: '#000000',
                                    fontWeight: '600',
                                }),
                                option: (base, state) => ({
                                    ...base,
                                    color: '#000000',
                                    backgroundColor: state.isFocused ? '#e9ecef' : '#ffffff',
                                }),
                            }}
                        />
                    </div>

                    <div style={{ minWidth: '150px' }}>
                        <label className="form-label small fw-bold text-dark mb-1" style={{ color: '#000000' }}>Select Month</label>
                        <Input
                            type="select"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            className="bg-white border text-dark shadow-none fw-semibold"
                            style={{ fontSize: '0.9rem', color: '#000000' }}
                        >
                            {monthOptions.map((m, idx) => (
                                <option key={idx} value={m.value}>
                                    {m.label}
                                </option>
                            ))}
                        </Input>
                    </div>
                </div>
            </div>

            {loading ? (
                <Loader height="250px" />
            ) : podSummaryList.length > 0 ? (
                <div className="d-flex flex-column gap-4">
                    {podSummaryList.map((order: any, idx: number) => {
                        const dateStr = order.ShippingOrderDate ? moment(order.ShippingOrderDate).format('DD MMM YYYY') : 'N/A';
                        const outletName = order.OutletFromName || 'N/A';
                        const remark = order.PaymentRemark || '';
                        const invoiceAmount = order.InvoiceAmount || 0;
                        const invoiceFile = order.InvoiceFile || '';
                        const skus = Array.isArray(order.Shippingliness) ? order.Shippingliness : [];
                        const totalQty = skus.reduce((sum: number, sku: any) => sum + (Number(sku.Qty) || 0), 0);

                        return (
                            <Card key={idx} className="border shadow-sm">
                                <CardBody className="p-4" style={{ color: '#000000' }}>
                                    <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom flex-wrap gap-2">
                                        <div>
                                            <h5 className="fw-bold text-primary m-0">{outletName}</h5>
                                            <div className="text-muted small fw-semibold">Date: {dateStr} {remark ? `| Remark: ${remark}` : ''}</div>
                                        </div>

                                        {invoiceFile && (
                                            <Button
                                                color="primary"
                                                size="sm"
                                                className="fw-bold d-flex align-items-center gap-1"
                                                onClick={() => resolveMediaByIds(invoiceFile)}
                                            >
                                                <FeatherIconCom iconName="Camera" size={14} />
                                                <span>View Attachment ({invoiceFile.split(',').filter(Boolean).length})</span>
                                            </Button>
                                        )}
                                    </div>

                                    {/* SKU Table */}
                                    <Table responsive bordered hover className="mb-0 align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="fw-bold text-dark">SKU Name</th>
                                                <th className="fw-bold text-dark">Qty | Value</th>
                                                <th className="fw-bold text-dark text-center">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {skus.map((sku: any, sIdx: number) => (
                                                <tr key={sIdx}>
                                                    <td className="fw-semibold text-dark">{sku.ProductName || '-'}</td>
                                                    <td className="fw-bold text-dark">{sku.Qty} | ₹{sku.Rate || '0'}</td>
                                                    <td className="text-center">
                                                        <Button
                                                            color="link"
                                                            className="p-0 border-0 shadow-none"
                                                            onClick={() => {
                                                                setStatusDetails({
                                                                    dateTime: sku.CreatedDate ? moment(sku.CreatedDate).format('DD MMM YYYY') : dateStr,
                                                                    status: sku.Status,
                                                                    approvalDate: sku.ApprovalDate ? moment(sku.ApprovalDate).format('DD MMM YYYY') : '',
                                                                    remark: sku.Remark || '',
                                                                });
                                                                setStatusModalOpen(true);
                                                            }}
                                                        >
                                                            {getStatusBadge(sku.Status)}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr className="table-secondary fw-bold">
                                                <td className="text-dark">Total</td>
                                                <td className="text-dark">{totalQty} | ₹{invoiceAmount}</td>
                                                <td></td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </CardBody>
                            </Card>
                        );
                    })}
                </div>
            ) : (
                <div className="text-center py-5 border rounded bg-white">
                    <FeatherIconCom iconName="Inbox" size={48} className="mb-3 text-secondary" />
                    <h5 className="fw-bold text-dark">No POD Summary Found</h5>
                    <p className="text-muted small">No submitted POD summary data available for the selected position and month.</p>
                </div>
            )}

            {/* Status Details Modal */}
            <Modal isOpen={statusModalOpen} toggle={() => setStatusModalOpen(!statusModalOpen)} centered>
                <div className="modal-header p-3 border-bottom d-flex justify-content-between align-items-center">
                    <h5 className="modal-title fw-bold text-dark m-0">POD Status Details</h5>
                    <Button color="link" className="p-0 text-dark border-0 shadow-none" onClick={() => setStatusModalOpen(false)}>
                        <FeatherIconCom iconName="X" size={20} />
                    </Button>
                </div>
                <ModalBody className="p-4" style={{ color: '#000000' }}>
                    <div className="d-flex justify-content-between py-2 border-bottom">
                        <span className="fw-bold text-dark">POD Submitted On:</span>
                        <span className="fw-bold text-primary">{statusDetails.dateTime || 'N/A'}</span>
                    </div>
                    <div className="d-flex justify-content-between py-2 border-bottom">
                        <span className="fw-bold text-dark">Final Status:</span>
                        <span>{getStatusBadge(statusDetails.status)}</span>
                    </div>
                    <div className="d-flex justify-content-between py-2 border-bottom">
                        <span className="fw-bold text-dark">Approved / Rejected On:</span>
                        <span className="fw-bold text-secondary">{statusDetails.approvalDate || 'N/A'}</span>
                    </div>
                    <div className="d-flex justify-content-between py-2">
                        <span className="fw-bold text-dark">Remark:</span>
                        <span className="fw-bold text-dark">{statusDetails.remark || 'None'}</span>
                    </div>
                </ModalBody>
            </Modal>

            {/* Media Modal */}
            <Modal isOpen={mediaModalOpen} toggle={() => setMediaModalOpen(!mediaModalOpen)} centered size="lg">
                <div className="modal-header p-3 border-bottom d-flex justify-content-between align-items-center">
                    <h5 className="modal-title fw-bold text-dark m-0">POD Summary Attachments</h5>
                    <Button color="link" className="p-0 text-dark border-0 shadow-none" onClick={() => setMediaModalOpen(false)}>
                        <FeatherIconCom iconName="X" size={20} />
                    </Button>
                </div>
                <ModalBody className="p-4" style={{ color: '#000000' }}>
                    {mediaLoading ? (
                        <Loader height="150px" />
                    ) : (
                        <div>
                            <div className="d-flex gap-2 mb-3">
                                <Button
                                    color={selectedMediaTab === 'images' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setSelectedMediaTab('images')}
                                >
                                    Images ({currentMedia.images.length})
                                </Button>
                                <Button
                                    color={selectedMediaTab === 'pdfs' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setSelectedMediaTab('pdfs')}
                                >
                                    PDF Documents ({currentMedia.pdfs.length})
                                </Button>
                            </div>

                            {selectedMediaTab === 'images' && (
                                currentMedia.images.length > 0 ? (
                                    <Row className="g-3">
                                        {currentMedia.images.map((img, i) => (
                                            <Col md={6} key={i}>
                                                <div className="border rounded p-2 text-center bg-light">
                                                    <img src={img.url} alt={`Media ${i}`} style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }} />
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                ) : <div className="text-muted text-center py-3">No images available</div>
                            )}

                            {selectedMediaTab === 'pdfs' && (
                                currentMedia.pdfs.length > 0 ? (
                                    <div className="d-flex flex-column gap-2">
                                        {currentMedia.pdfs.map((pdf, i) => (
                                            <div key={i} className="d-flex justify-content-between align-items-center p-3 border rounded bg-light">
                                                <span className="fw-bold text-dark">{pdf.name}</span>
                                                <a href={pdf.pdfurl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary">
                                                    Open / Download PDF
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                ) : <div className="text-muted text-center py-3">No PDF documents available</div>
                            )}
                        </div>
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
}
